import { Box, Tab, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react';
import React from 'react';

import type { DocTabItem } from '../../config';
import { useCodingConfig } from '../../interface';
import { STRIP_BG, STRIP_BORDER, STRIP_H } from '../../styles/tokens';
import { useTabSelection } from '../TabSelectionContext';

/** Ascending by `order`, dropping any tab marked `visible:false` -- same ordering/visibility contract
 *  as PatientSummaryBar/CaseContextBar's `visibleFieldsInOrder`, kept local to this molecule folder
 *  per that precedent (each molecule folder stays self-contained). `.filter` on a `readonly
 *  DocTabItem[]` already returns a fresh mutable array, so `.sort` never mutates the config's own
 *  (readonly, `as const`) tab list. */
function visibleTabsInOrder(tabs: readonly DocTabItem[]): DocTabItem[] {
  return tabs.filter((tab) => tab.visible).sort((a, b) => a.order - b.order);
}

/**
 * LEFT-pane document tab strip. `left.tabs` is `[]` in
 * `DEFAULT_CODING_SCREEN_CONFIG` so by default this renders an empty, labelled
 * `role="region"` container with NO tabs at all -- "Default empty shell OMITS tabs
 * entirely (no inert/disabled tabs without panels)". When a config supplies tabs, each visible one
 * renders as a Chakra `Tab` in a `TabList`, paired POSITIONALLY with an (empty, shell-only) `TabPanel`
 * in `TabPanels` -- Chakra pairs a `Tab` with its `TabPanel` by mount ORDER within each list, not by
 * id, so both lists map over the SAME sorted/filtered `visibleTabs` array here, guaranteeing a Tab and
 * its TabPanel can never drift out of correspondence. This satisfies both the atom-reuse requirement
 * and "a Tab needs a matching TabPanel" a11y rule at once.
 *
 * Each `TabPanel` is an empty shell-only placeholder (real content lives in the sibling
 * `ClinicalDocumentViewer`, not here) -- but Chakra 2.3.7's default Tabs base style still gives the
 * active `TabPanel` `p:4`, which would spill roughly 32px below this strip's fixed height
 * (`STRIP_H`, styles/tokens.ts). Zeroing each panel's padding removes that content height, and
 * clipping the outer `Tabs` root with `overflow:hidden` at its fixed height is the
 * belt-and-suspenders guarantee that nothing inside can ever grow the strip, without touching
 * Chakra's normal selected/non-selected panel `hidden`-attribute wiring (so the tab<->panel pairing
 * above is untouched).
 *
 * CONTROLLED SELECTION: `<Tabs>` is now driven by `useTabSelection()`
 * (scoped per pane by the ancestor `TabSelectionProvider` -- `organisms/DocumentationPane`) instead
 * of Chakra's own uncontrolled internal state -- `index` is the position of the currently-selected
 * `contentKey` within `visibleTabs` (falling back to `0`, i.e. the first tab, when nothing is
 * selected yet or the selection doesn't match any currently-visible tab), and `onChange` calls
 * `select` with the newly-clicked tab's `contentKey`. The clicked tab's actual CONTENT still doesn't
 * render here -- `TabPanel`s stay empty shells -- `ClinicalDocumentViewer` reads the same
 * `useTabSelection()` value to render the selected tab's slot in the pane BODY.
 */
export const DocumentTitleNav: React.FC = () => {
  const { docNav, tabs } = useCodingConfig().left;
  const visibleTabs = visibleTabsInOrder(tabs);
  const { selectedContentKey, select } = useTabSelection();

  if (visibleTabs.length === 0) {
    return (
      <Box
        role="region"
        aria-label="Document navigation"
        data-content-key={docNav.contentKey}
        flexShrink={0}
        h={STRIP_H}
        minH={STRIP_H}
        bg={STRIP_BG}
        borderBottom={`1px solid ${STRIP_BORDER}`}
      />
    );
  }

  const index = Math.max(
    0,
    visibleTabs.findIndex((tab) => tab.contentKey === selectedContentKey)
  );

  return (
    <Tabs
      role="region"
      aria-label="Document navigation"
      data-content-key={docNav.contentKey}
      flexShrink={0}
      h={STRIP_H}
      minH={STRIP_H}
      bg={STRIP_BG}
      borderBottom={`1px solid ${STRIP_BORDER}`}
      style={{ overflow: 'hidden' }}
      index={index}
      onChange={(i) => select(visibleTabs[i].contentKey)}
    >
      <TabList>
        {visibleTabs.map((tab) => (
          <Tab key={tab.contentKey} data-slot-key={tab.contentKey}>
            {tab.label}
          </Tab>
        ))}
      </TabList>
      <TabPanels>
        {visibleTabs.map((tab) => (
          <TabPanel key={tab.contentKey} style={{ padding: 0 }} />
        ))}
      </TabPanels>
    </Tabs>
  );
};
